import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useSession } from "../hooks/useSession";
import { redirectAfterPasswordAuth } from "../lib/storePasswordCredential.js";
import { Button } from "./ui/Button.js";
import { Input, Label } from "./ui/Input.js";
import { AuthScaffold } from "./auth/AuthScaffold.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

const ERROR_MESSAGES: Record<string, string> = {
  oauth_cancelled: "Sign-in was cancelled. Please try again.",
  unauthorized_domain: "This Google account is not authorized. Use the email you applied with.",
  unauthorized_user:
    "No account found for this Google email. Apply first if you are a new agent.",
  oauth_failed: "Something went wrong during sign-in. Please try again.",
  invalid_invite: "This invite link is invalid. Please ask an administrator for a new invite.",
  invite_expired: "This invite link has expired. Please ask an administrator for a new invite.",
  invite_already_accepted: "This invite has already been accepted. Please sign in with the account that accepted it.",
  email_in_use: "That email is already linked to another account. Use a different email or sign in instead.",
  invite_target_unavailable: "This agent record is no longer available to claim. Ask staff for a new link.",
};

interface InvitePreview {
  valid: boolean;
  kind?: "agent-claim" | "staff";
  agentDisplayName?: string | null;
  code?: string;
  inviterName?: string | null;
  organization?: {
    brandName: string;
  };
}

export function GoogleLogin() {
  const { loading, oauthError } = useSession();
  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get("invite")?.trim() ?? "";
  const returnTo = params.get("returnTo")?.trim() ?? "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [invitePreview, setInvitePreview] = useState<InvitePreview | null>(null);

  const isStaffInvite = Boolean(inviteToken) && invitePreview?.kind === "staff";
  const isAgentClaimInvite = Boolean(inviteToken) && invitePreview?.kind === "agent-claim";
  const isInviteContext = Boolean(inviteToken);
  const brandName = invitePreview?.organization?.brandName ?? "Altura";

  useEffect(() => {
    if (!inviteToken) {
      setInvitePreview(null);
      return;
    }

    async function loadInvitePreview() {
      try {
        const res = await fetch(`${API_BASE}/api/user-invites/validate/${encodeURIComponent(inviteToken)}`);
        const json = await res.json();
        if (!res.ok || !json.success) return;
        setInvitePreview(json.data as InvitePreview);
      } catch {
        setInvitePreview({ valid: false, code: "invalid_invite" });
      }
    }

    void loadInvitePreview();
  }, [inviteToken]);

  function handleGoogleSignIn() {
    if (returnTo.startsWith("/")) {
      sessionStorage.setItem("aam_post_login_redirect", returnTo);
    }
    const url = new URL(`${API_BASE}/api/auth/google`);
    if (inviteToken) url.searchParams.set("invite", inviteToken);
    window.location.href = url.toString();
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const endpoint = inviteToken ? "/api/auth/accept-invite" : "/api/auth/login";
      const body = inviteToken
        ? { email, password, invite: inviteToken }
        : { email, password };

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(ERROR_MESSAGES[json.code as string] ?? json.error ?? "Login failed");
      }

      localStorage.setItem("aam_session_token", json.data.sessionToken);
      const user = json.data;
      const redirect =
        returnTo.startsWith("/")
          ? returnTo
          : user.isStaff
            ? "/dashboard/agents"
            : "/my-agent";
      await redirectAfterPasswordAuth(email, password, redirect);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Login failed");
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AuthScaffold>
        <div className="w-full" role="status" aria-live="polite">
          <div className="mb-6 h-2 w-24 overflow-hidden rounded-full bg-surface-muted">
            <div className="h-full w-1/2 animate-pulse bg-accent-strong" />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-content-primary">Securing your workspace…</h1>
          <p className="mt-3 text-content-secondary">We’re checking your session. This should only take a moment.</p>
        </div>
      </AuthScaffold>
    );
  }

  return (
    <AuthScaffold
      brandName={brandName}
      eyebrow={isInviteContext ? "Your invitation is ready" : "Welcome back"}
      title={isInviteContext ? "A better way to build, together." : "Your whole business. One clear view."}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full space-y-7"
      >
        <Link to="/" className="focus-ring inline-flex rounded text-sm font-bold text-content-muted hover:text-content-primary">
          ← Back home
        </Link>

        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-accent">{isInviteContext ? "Invitation" : "Secure sign in"}</p>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-[-0.04em] text-content-primary">
            {isAgentClaimInvite
              ? "Claim your agent portal"
              : isStaffInvite
                ? "Accept your invite"
                : "Welcome back"}
          </h1>
          <p className="mt-3 max-w-md leading-relaxed text-content-secondary">
            {isAgentClaimInvite
              ? invitePreview?.agentDisplayName
                ? `Link your login to ${invitePreview.agentDisplayName}'s contracting record. Use the email you want on your portal account.`
                : "Link your login to your contracting record. Use the email you want on your portal account."
              : isStaffInvite
                ? `${invitePreview?.inviterName ?? "Your administrator"} has invited you to join the ${brandName} organization. Accept with Google or by creating a password.`
                : "Sign in to view contract status, manage your profile, and access agency tools."}
          </p>
        </div>

        {(oauthError || formError) && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            role="alert"
            className="rounded-[10px] border border-status-rejected-border bg-status-rejected-bg px-4 py-3 text-sm font-medium text-status-rejected-text"
          >
            {ERROR_MESSAGES[oauthError ?? ""] ?? formError}
          </motion.div>
        )}

        <div className="space-y-5 rounded-[12px] border border-border-default bg-surface-raised p-5 shadow-card sm:p-6">
          <form onSubmit={handlePasswordLogin} className="space-y-3">
            <Label>
              Email
              <Input
                type="email"
                name="username"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </Label>
            <Label>
              Password
              <Input
                type="password"
                name={isInviteContext ? "new-password" : "password"}
                required
                autoComplete={isInviteContext ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </Label>
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? "Signing in..." : isInviteContext ? "Accept invite" : "Sign in"}
            </Button>
            {isInviteContext && (
              <p className="text-xs text-content-muted">
                {isAgentClaimInvite
                  ? "Use the email you want on your agent portal account."
                  : "Choose a password for this email to finish accepting the invite."}
              </p>
            )}
          </form>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border-default" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-surface-page px-3 font-semibold text-content-muted">or continue with</span>
          </div>
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="focus-ring flex min-h-12 w-full items-center justify-center gap-3 rounded-[10px] border border-border-default bg-surface-raised px-4 py-3 text-sm font-bold text-content-primary shadow-card transition-colors hover:bg-surface-overlay"
        >
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {isInviteContext ? "Accept invite with Google" : "Staff login"}
        </button>
      </motion.div>
    </AuthScaffold>
  );
}
